import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Play, Search, Calendar, Music2, Radio, Mic } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human-readable platform label + subtle button classes. Any DJ can pick
// their own hosting service; we just link out to it.
const PLATFORMS = {
  soundcloud: { label: "SoundCloud", color: "bg-orange-600 hover:bg-orange-500" },
  mixcloud: { label: "Mixcloud", color: "bg-sky-600 hover:bg-sky-500" },
  youtube: { label: "YouTube", color: "bg-red-600 hover:bg-red-500" },
  spotify: { label: "Spotify", color: "bg-emerald-600 hover:bg-emerald-500" },
  other: { label: "Listen", color: "bg-zinc-700 hover:bg-zinc-600" },
};

const TYPE_LABELS = {
  show: "Show",
  interview: "Interview",
  show_interview: "Show + Interview",
};

// A "channel" row = { dj_slug, dj_name, dj_photo, soundcloud, mixcloud, ... }
// Yields a list of {platform, url, label} in the order we want buttons to
// render on each DJ card. Empty platforms are skipped.
function channelButtons(channel) {
  const order = ["soundcloud", "mixcloud", "youtube", "spotify"];
  const out = [];
  for (const key of order) {
    if (channel[key]) {
      out.push({ platform: key, url: channel[key], label: PLATFORMS[key].label });
    }
  }
  if (channel.other_url) {
    out.push({
      platform: "other",
      url: channel.other_url,
      label: (channel.other_label || "Listen").trim(),
    });
  }
  return out;
}

function formatDate(iso) {
  if (!iso) return "";
  // Accept both "YYYY-MM-DD" and full ISO. Show as "Nov 12, 2026".
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
  });
}

// Big header card at the top: title + tagline. Kept simple and heavy — the
// glitch class is already defined in the site's global CSS and matches
// other page headings.
function ArchiveHero() {
  return (
    <section className="relative border-b border-zinc-800 px-4 md:px-10 pt-16 pb-12">
      <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
        //_THE_ARCHIVE
      </div>
      <h1
        className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tighter text-zinc-50 glitch"
        data-testid="archive-hero-title"
      >
        ARCHIVED <span className="text-red-600">SHOWS</span> &amp; INTERVIEWS.
      </h1>
      <p className="mt-6 max-w-2xl text-sm md:text-base text-zinc-100 leading-relaxed">
        Missed it live? Every show and interview our DJs have archived is one
        click away. Pick a broadcaster below and dive into their catalog on
        whichever platform they upload to.
      </p>
    </section>
  );
}

// Horizontal-scroll strip of the newest entries across all DJs. Only shown
// when we actually have 1+ entry logged.
function LatestDropsStrip({ entries }) {
  if (!entries.length) return null;
  const latest = entries.slice(0, 8);
  return (
    <section
      className="border-b border-zinc-800 px-4 md:px-10 py-10"
      data-testid="archive-latest-drops"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="on-air-dot" aria-hidden />
        <h2 className="font-display text-2xl md:text-3xl tracking-tight text-zinc-50">
          Latest <span className="text-red-600">Drops</span>
        </h2>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 md:-mx-10 px-4 md:px-10 snap-x snap-mandatory">
        {latest.map((e) => (
          <EntryCard key={e.id} entry={e} compact />
        ))}
      </div>
    </section>
  );
}

// Single entry (show or interview) row/card. Renders as either a compact
// horizontal-scroll card (used in Latest Drops) or a full-width row inside
// a DJ block.
function EntryCard({ entry, compact = false }) {
  const platform = PLATFORMS[entry.platform] || PLATFORMS.other;
  if (compact) {
    return (
      <a
        href={entry.link}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative flex-shrink-0 snap-start w-72 md:w-80 border border-zinc-800 hover:border-red-600 bg-zinc-950 p-5 transition-colors"
        data-testid={`archive-entry-card-${entry.id}`}
      >
        <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2">
          <span>{TYPE_LABELS[entry.entry_type] || "Show"}</span>
          {entry.episode_date && (
            <>
              <span>·</span>
              <span>{formatDate(entry.episode_date)}</span>
            </>
          )}
        </div>
        <div className="font-display text-lg text-zinc-50 group-hover:text-red-500 leading-tight line-clamp-2">
          {entry.title}
        </div>
        <div className="mt-2 text-xs text-zinc-400 uppercase tracking-widest">
          {entry.dj_name}
        </div>
        {entry.band_featured && (
          <div className="mt-1 text-xs text-red-500 uppercase tracking-widest">
            with {entry.band_featured}
          </div>
        )}
        <div className={`mt-4 inline-flex items-center gap-2 px-3 py-2 text-xs tracking-widest uppercase text-white ${platform.color} transition-colors`}>
          <Play className="w-3.5 h-3.5" fill="currentColor" />
          {platform.label}
        </div>
      </a>
    );
  }
  return (
    <a
      href={entry.link}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 border-t border-zinc-800 py-3 hover:bg-black/40 -mx-2 px-2 transition-colors"
      data-testid={`archive-entry-row-${entry.id}`}
    >
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-zinc-900 border border-zinc-800 group-hover:border-red-600 group-hover:text-red-500 text-zinc-400 transition-colors">
        {entry.entry_type === "interview" ? (
          <Mic className="w-4 h-4" />
        ) : (
          <Radio className="w-4 h-4" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base text-zinc-50 group-hover:text-red-500 truncate">
          {entry.title}
        </div>
        <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-1">
          <span>{TYPE_LABELS[entry.entry_type] || "Show"}</span>
          {entry.band_featured && (
            <>
              <span>·</span>
              <span className="text-red-500">with {entry.band_featured}</span>
            </>
          )}
          {entry.episode_date && (
            <>
              <span>·</span>
              <span>{formatDate(entry.episode_date)}</span>
            </>
          )}
        </div>
      </div>
      <div className={`flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-widest uppercase text-white ${platform.color} transition-colors`}>
        <Play className="w-3.5 h-3.5" fill="currentColor" />
        {platform.label}
      </div>
    </a>
  );
}

// One card per DJ: photo, name, buttons for each platform channel they
// gave us, and a list of the individual entries they've logged (if any).
function DJArchiveCard({ channel, entries }) {
  const buttons = channelButtons(channel);
  const djEntries = entries.filter((e) => e.dj_slug === channel.dj_slug);
  const hasEntries = djEntries.length > 0;
  const shown = djEntries.slice(0, 5);
  const more = djEntries.length - shown.length;

  return (
    <article
      className="border border-zinc-800 bg-zinc-950 hover:border-zinc-700 transition-colors"
      data-testid={`archive-dj-card-${channel.dj_slug}`}
    >
      {/* Top: photo + name + platform buttons */}
      <div className="flex items-start gap-4 p-5 border-b border-zinc-800">
        <Link
          to={`/djs/${channel.dj_slug}`}
          className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-zinc-900 border border-zinc-800 overflow-hidden hover:border-red-600 transition-colors"
        >
          {channel.dj_photo ? (
            <img
              src={channel.dj_photo}
              alt={channel.dj_name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-700">
              <Music2 className="w-6 h-6" />
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <Link
            to={`/djs/${channel.dj_slug}`}
            className="font-display text-xl md:text-2xl text-zinc-50 hover:text-red-500 tracking-tight block"
          >
            {channel.dj_name}
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-1">
            Broadcaster
          </div>
          {buttons.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {buttons.map((b) => (
                <a
                  key={b.platform + b.url}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase text-white ${PLATFORMS[b.platform].color} transition-colors`}
                  data-testid={`archive-channel-btn-${channel.dj_slug}-${b.platform}`}
                >
                  <Play className="w-3 h-3" fill="currentColor" />
                  {b.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Entries list */}
      {hasEntries && (
        <div className="p-5">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">
            Recent Episodes
          </div>
          <div>
            {shown.map((e) => (
              <EntryCard key={e.id} entry={e} />
            ))}
          </div>
          {more > 0 && (
            <div className="mt-3 text-xs text-zinc-500 tracking-widest uppercase">
              + {more} more on their channel
            </div>
          )}
        </div>
      )}
      {!hasEntries && (
        <div className="p-5 text-xs text-zinc-500 tracking-widest uppercase">
          Browse their full catalog on the buttons above.
        </div>
      )}
    </article>
  );
}

export default function Archive() {
  const [channels, setChannels] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedDj, setSelectedDj] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [chRes, enRes] = await Promise.all([
          axios.get(`${API}/archive/channels`),
          axios.get(`${API}/archive/entries`),
        ]);
        setChannels(chRes.data || []);
        setEntries(enRes.data || []);
      } catch (e) {
        /* silent — page renders empty state */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Union of DJs that show up: any DJ with a channel row OR at least one
  // entry. Sorted alphabetically.
  const allDjs = useMemo(() => {
    const map = new Map();
    for (const c of channels) map.set(c.dj_slug, { slug: c.dj_slug, name: c.dj_name });
    for (const e of entries) {
      if (!map.has(e.dj_slug)) map.set(e.dj_slug, { slug: e.dj_slug, name: e.dj_name });
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [channels, entries]);

  const filteredChannels = useMemo(() => {
    let list = channels;
    if (selectedDj !== "all") list = list.filter((c) => c.dj_slug === selectedDj);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((c) => c.dj_name.toLowerCase().includes(needle));
    }
    return list;
  }, [channels, selectedDj, q]);

  const filteredEntries = useMemo(() => {
    let list = entries;
    if (selectedDj !== "all") list = list.filter((e) => e.dj_slug === selectedDj);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((e) =>
        e.title.toLowerCase().includes(needle) ||
        e.dj_name.toLowerCase().includes(needle) ||
        (e.band_featured || "").toLowerCase().includes(needle),
      );
    }
    return list;
  }, [entries, selectedDj, q]);

  const isEmpty = !loading && filteredChannels.length === 0 && filteredEntries.length === 0;

  return (
    <div className="relative min-h-screen" data-testid="archive-page">
      <Header />
      <ArchiveHero />

      {/* Search + DJ filter */}
      {!loading && (channels.length > 0 || entries.length > 0) && (
        <section className="border-b border-zinc-800 px-4 md:px-10 py-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex items-center gap-2 border border-zinc-800 bg-zinc-950 px-3 py-2 flex-1 md:max-w-md">
              <Search className="w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search DJs, episodes, bands..."
                className="bg-transparent outline-none text-sm text-zinc-100 placeholder:text-zinc-600 flex-1"
                data-testid="archive-search-input"
              />
            </div>
            <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-zinc-500">
              <Calendar className="w-3.5 h-3.5" />
              <select
                value={selectedDj}
                onChange={(e) => setSelectedDj(e.target.value)}
                className="bg-zinc-950 border border-zinc-800 text-zinc-100 px-3 py-2 text-xs tracking-widest uppercase outline-none"
                data-testid="archive-dj-filter"
              >
                <option value="all">All DJs</option>
                {allDjs.map((d) => (
                  <option key={d.slug} value={d.slug}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>
      )}

      <LatestDropsStrip entries={filteredEntries} />

      {/* Main grid: browse by DJ */}
      <section className="px-4 md:px-10 py-10">
        {loading && (
          <div className="text-zinc-300 text-sm tracking-widest uppercase">
            //_loading archive...
          </div>
        )}

        {isEmpty && (
          <div
            className="border border-zinc-800 bg-zinc-950 p-10 text-center"
            data-testid="archive-empty-state"
          >
            <div className="font-display text-2xl md:text-3xl text-zinc-50 mb-3">
              Archive coming online.
            </div>
            <p className="text-sm text-zinc-400 max-w-lg mx-auto leading-relaxed">
              DJs are lining up their SoundCloud, Mixcloud &amp; YouTube channels
              to share past shows and interviews here. Check back soon — or
              tune in <Link to="/schedule" className="text-red-500 hover:underline">live on the schedule</Link>.
            </p>
          </div>
        )}

        {!loading && filteredChannels.length > 0 && (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="on-air-dot" aria-hidden />
              <h2 className="font-display text-2xl md:text-3xl tracking-tight text-zinc-50">
                Browse by <span className="text-red-600">DJ</span>
              </h2>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              data-testid="archive-dj-grid"
            >
              {filteredChannels.map((c) => (
                <DJArchiveCard key={c.dj_slug} channel={c} entries={filteredEntries} />
              ))}
            </div>
          </>
        )}

        {/* Nudge to other DJs */}
        {!loading && (channels.length > 0 || entries.length > 0) && (
          <div className="mt-16 text-center text-xs text-zinc-500 tracking-widest uppercase">
            Want your show archived here?{" "}
            <a href="mailto:sondra@digitalrevolutionradio.com" className="text-red-500 hover:underline">
              Talk to Sondra
            </a>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
